import React, { useState } from 'react';
import { Clock, CheckCircle, Check, ChevronDown, ChevronRight, CornerUpRight } from 'lucide-react';
import { deadlineTypeLabel } from '../../utils/litigationLabels';

function daysUntil(iso) { return Math.round((new Date(iso) - Date.now()) / 86400000); }
function relative(iso) {
  const n = daysUntil(iso);
  if (n === 0) return 'today';
  if (n < 0) return `${-n} day${-n > 1 ? 's' : ''} ago`;
  return `in ${n} day${n > 1 ? 's' : ''}`;
}
function fmt(iso) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
// Urgency band (matches Virgo's CaseDeadlines scheme): red past-due/<3, amber 3-7, yellow 8-14, green >14.
function band(iso) {
  const n = daysUntil(iso);
  if (n < 0) return { dot: 'bg-red-500', box: 'bg-red-900/30 border-red-500/40', text: 'text-red-300' };
  if (n <= 3) return { dot: 'bg-red-500', box: 'bg-red-900/30 border-red-500/40', text: 'text-red-300' };
  if (n <= 7) return { dot: 'bg-amber-500', box: 'bg-amber-900/30 border-amber-500/40', text: 'text-amber-300' };
  if (n <= 14) return { dot: 'bg-yellow-500', box: 'bg-yellow-900/20 border-yellow-500/30', text: 'text-yellow-300' };
  return { dot: 'bg-green-500', box: 'bg-virgo-navy-800/50 border-virgo-teal-700/30', text: 'text-green-300' };
}

function DeadlineRow({ d, onAcknowledge, onResolve, onViewEvent }) {
  const b = band(d.deadline_date);
  const resolved = d.status === 'resolved';
  return (
    <div className={`p-4 rounded-xl border ${resolved ? 'bg-virgo-navy-800/40 border-virgo-teal-700/20 opacity-70' : b.box}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-3 h-3 mt-1.5 rounded-full shrink-0 ${resolved ? 'bg-virgo-teal-600' : b.dot}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-white">{deadlineTypeLabel(d.deadline_type)}</p>
              {d.status !== 'pending' && (
                <span className="px-2 py-0.5 rounded-full text-xs border border-virgo-teal-700/40 text-virgo-teal-300 capitalize">{d.status}</span>
              )}
            </div>
            <p className={`text-sm mt-0.5 ${resolved ? 'text-virgo-teal-400' : b.text}`}>
              {fmt(d.deadline_date)} · <span className="font-medium">{relative(d.deadline_date)}</span>
              {d.precision === 'hour' && <span className="text-virgo-teal-500"> (hour-precise)</span>}
            </p>
            <p className="text-xs text-virgo-teal-400 mt-1">{d.authority}</p>
            {d.caveats && d.caveats.length > 0 && (
              <ul className="mt-1.5 space-y-0.5">
                {d.caveats.map((c, i) => <li key={i} className="text-xs text-virgo-teal-500">⚠ {c}</li>)}
              </ul>
            )}
            <button onClick={() => onViewEvent(d.litigation_event_id)}
              className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-1.5">
              <CornerUpRight className="w-3 h-3" /> View triggering event
            </button>
          </div>
        </div>
        {!resolved && (
          <div className="flex flex-col gap-2 shrink-0">
            {d.status === 'pending' && (
              <button onClick={() => onAcknowledge(d.id)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded-lg hover:bg-amber-500/25 transition-colors">
                <Check className="w-3 h-3" /> Acknowledge
              </button>
            )}
            <button onClick={() => onResolve(d.id)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-green-300 bg-green-500/15 border border-green-500/30 rounded-lg hover:bg-green-500/25 transition-colors">
              <CheckCircle className="w-3 h-3" /> Resolve
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const LitigationDeadlines = ({ deadlines, onAcknowledge, onResolve, onViewEvent }) => {
  const [showDone, setShowDone] = useState(false);
  const active = (deadlines || []).filter((d) => d.status === 'pending' || d.status === 'acknowledged');
  const done = (deadlines || []).filter((d) => d.status === 'resolved');
  const sortByDate = (a, b) => new Date(a.deadline_date) - new Date(b.deadline_date);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-cyan-400" />
        <h4 className="text-lg font-semibold text-white">Upcoming Deadlines</h4>
        {active.length > 0 && (
          <span className="px-2 py-0.5 bg-cyan-500/20 rounded-full text-xs text-cyan-300 border border-cyan-500/30">{active.length}</span>
        )}
      </div>

      {active.length === 0 ? (
        <div className="text-center py-8 bg-virgo-navy-800/30 rounded-xl border border-virgo-teal-700/30">
          <p className="text-virgo-teal-400 text-sm">No pending deadlines. Deadlines are computed automatically when you log an event.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {active.sort(sortByDate).map((d) => (
            <DeadlineRow key={d.id} d={d} onAcknowledge={onAcknowledge} onResolve={onResolve} onViewEvent={onViewEvent} />
          ))}
        </div>
      )}

      {done.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowDone(!showDone)}
            className="inline-flex items-center gap-1 text-sm text-virgo-teal-400 hover:text-virgo-teal-200">
            {showDone ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Show resolved ({done.length})
          </button>
          {showDone && (
            <div className="space-y-3 mt-3">
              {done.sort(sortByDate).map((d) => (
                <DeadlineRow key={d.id} d={d} onAcknowledge={onAcknowledge} onResolve={onResolve} onViewEvent={onViewEvent} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LitigationDeadlines;
